import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "@/components/ui/sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Plus, Receipt, Loader2, ExternalLink, Upload, FileSpreadsheet, Download, CheckCircle2, AlertTriangle, Package, RefreshCw, Copy, ArrowLeftRight, Clock, FileDiff, ArrowRight, Send, Info } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WAREHOUSES = [
  { code: "UK", name: "United Kingdom", ccy: "EUR" },
  { code: "AUS", name: "Australia", ccy: "AUD" },
  { code: "CH", name: "Switzerland", ccy: "CHF" },
  { code: "CA", name: "Canada", ccy: "CAD" },
  { code: "IN", name: "India", ccy: "INR" },
];
const CURRENCIES = ["EUR", "AUD", "CAD", "CHF", "INR", "USD"];
const STATUSES = [
  ["in_stock", "In stock"], ["unfinished", "Unfinished"], ["in_transit", "In transit"],
  ["overstock", "Overstock"],
];

const field = "w-full h-9 px-2.5 text-sm rounded-md bg-[hsl(var(--background))] border border-[hsl(var(--border))] text-[hsl(var(--foreground))] focus:outline-none focus:ring-2 focus:ring-emerald-500";
const label = "text-[11px] font-medium text-[hsl(var(--muted-foreground))] block mb-1";

function SubmitBtn({ saving, icon: Icon, children }) {
  return (
    <button type="submit" disabled={saving}
      className="inline-flex items-center gap-1.5 h-9 px-4 rounded-md text-[13px] font-semibold text-white disabled:opacity-60"
      style={{ backgroundColor: "#0F6E56" }}>
      {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Icon className="w-4 h-4" />}
      {children}
    </button>
  );
}

// ── Receive a new bit into a warehouse ──
export function ReceiveBitModal({ defaultWarehouse, onClose, onDone }) {
  const [saving, setSaving] = useState(false);
  const initialWh = (defaultWarehouse && defaultWarehouse !== "ALL") ? defaultWarehouse : "UK";
  const [f, setF] = useState({
    warehouse: initialWh,
    marking_no: "", design_no: "", status: "in_stock",
    blades: "", body_type: "", bit_size_in: "", bit_size_mm: "", thread_size: "",
    nozzle_count: "", nozzle_size: "", gauge_cutter_qty: "", gauge_cutter_size: "",
    pdc_qty: "", pdc_size: "", spec_sheet_url: "",
    price: "", currency: WAREHOUSES.find(w => w.code === initialWh)?.ccy || "",
    shelf_location: "", supplier: "",
  });
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));
  const onWh = (code) => setF(p => ({ ...p, warehouse: code, currency: WAREHOUSES.find(w => w.code === code)?.ccy || p.currency }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.marking_no.trim() && !f.design_no.trim()) { toast.error("Enter a marking or design number"); return; }
    setSaving(true);
    try {
      // Tricone is stored via body_type, not blades; mirror the spec link.
      const payload = { ...f, spec_sheet: f.spec_sheet_url };
      if (f.blades === "Tricone") { payload.blades = ""; payload.body_type = payload.body_type || "TRICONE BIT"; }
      await axios.post(`${API}/stock-center/bits`, payload);
      toast.success("Bit received");
      onDone?.(); onClose();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to receive bit"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      {/* Cap the dialog at the viewport and lay it out as a column: fixed header,
          scrollable field body, pinned footer — so a tall form (esp. when fields
          stack single-column on a phone) never spills past the screen with the
          Receive/Cancel buttons unreachable. */}
      <DialogContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))] max-w-lg flex flex-col max-h-[90vh] p-0" data-testid="receive-bit-modal">
        <DialogHeader className="px-6 pt-6 shrink-0">
          <DialogTitle className="flex items-center gap-2"><Plus className="w-5 h-5 text-[#0F6E56]" /> Receive bit</DialogTitle>
          <DialogDescription>Add a drill bit into a warehouse.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="flex flex-col min-h-0 flex-1">
          {/* Scrollable field area — single column on phones (≤sm) so each field
              gets a full-width tap target; 2-up grid from sm up. The cutter/nozzle
              qty×size pairs keep their own inner 2-up (they read as one unit). */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 overflow-y-auto px-6 py-1">
            <div><span className={label}>Warehouse</span>
              <select value={f.warehouse} onChange={e => onWh(e.target.value)} className={field}>
                {WAREHOUSES.map(w => <option key={w.code} value={w.code}>{w.name}</option>)}
              </select>
            </div>
            <div><span className={label}>Status</span>
              <select value={f.status} onChange={e => set("status", e.target.value)} className={field}>
                {STATUSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div><span className={label}>Bit type</span>
              <select value={f.blades} onChange={e => set("blades", e.target.value)} className={field} data-testid="receive-blades">
                <option value="">— select —</option>
                {["3 Blade", "4 Blade", "5 Blade", "6 Blade", "Tricone", "Other"].map(b => <option key={b} value={b}>{b === "Tricone" ? "VRS / Tricone" : b}</option>)}
              </select>
            </div>
            <div><span className={label}>Body type</span>
              <input value={f.body_type} onChange={e => set("body_type", e.target.value)} className={field} placeholder="PDC drill bit…" /></div>
            <div><span className={label}>Marking no.</span>
              <input value={f.marking_no} onChange={e => set("marking_no", e.target.value)} className={field} placeholder="Serial" data-testid="receive-marking" /></div>
            <div><span className={label}>Design no.</span>
              <input value={f.design_no} onChange={e => set("design_no", e.target.value)} className={field} placeholder="Design code" /></div>
            <div><span className={label}>Bit size (inch)</span>
              <input value={f.bit_size_in} onChange={e => set("bit_size_in", e.target.value)} className={field} placeholder='e.g. 3-7/8"' /></div>
            <div><span className={label}>Thread</span>
              <input value={f.thread_size} onChange={e => set("thread_size", e.target.value)} className={field} placeholder='e.g. 2-3/8" API REG PIN' /></div>
            <div className="col-span-1 sm:col-span-2"><span className={label}>Spec sheet link (SharePoint)</span>
              <input value={f.spec_sheet_url} onChange={e => set("spec_sheet_url", e.target.value)} className={field} placeholder="https://…sharepoint.com/…" data-testid="receive-spec" /></div>
            <div><span className={label}>Nozzles (count × size)</span>
              <div className="flex gap-1.5"><input value={f.nozzle_count} onChange={e => set("nozzle_count", e.target.value)} className={field} placeholder="#" /><input value={f.nozzle_size} onChange={e => set("nozzle_size", e.target.value)} className={field} placeholder="size" /></div></div>
            <div><span className={label}>Gauge cutters (qty × size)</span>
              <div className="flex gap-1.5"><input value={f.gauge_cutter_qty} onChange={e => set("gauge_cutter_qty", e.target.value)} className={field} placeholder="qty" /><input value={f.gauge_cutter_size} onChange={e => set("gauge_cutter_size", e.target.value)} className={field} placeholder="size" /></div></div>
            <div><span className={label}>PDC cutters (qty × size)</span>
              <div className="flex gap-1.5"><input value={f.pdc_qty} onChange={e => set("pdc_qty", e.target.value)} className={field} placeholder="qty" /><input value={f.pdc_size} onChange={e => set("pdc_size", e.target.value)} className={field} placeholder="size" /></div></div>
            <div><span className={label}>Price</span>
              <input value={f.price} onChange={e => set("price", e.target.value)} className={field} placeholder="Amount" /></div>
            <div><span className={label}>Currency</span>
              <select value={f.currency} onChange={e => set("currency", e.target.value)} className={field}>
                {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div><span className={label}>Shelf location</span>
              <input value={f.shelf_location} onChange={e => set("shelf_location", e.target.value)} className={field} /></div>
            <div><span className={label}>Supplier</span>
              <input value={f.supplier} onChange={e => set("supplier", e.target.value)} className={field} /></div>
          </div>
          {/* Pinned footer — stays put while the field area scrolls, so the
              Receive/Cancel buttons are always reachable on a phone. */}
          <DialogFooter className="gap-2 px-6 py-4 border-t border-[hsl(var(--border))] shrink-0 bg-[hsl(var(--card))]">
            <button type="button" onClick={onClose} className="h-9 px-4 rounded-md text-[13px] border border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))]">Cancel</button>
            <SubmitBtn saving={saving} icon={Plus}>Receive</SubmitBtn>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ── Mark a bit sold (stock-out). No ERP mirror in this half. ──
export function SellBitModal({ bit, onClose, onDone }) {
  const [saving, setSaving] = useState(false);
  const [clientNames, setClientNames] = useState([]);
  const [mirror, setMirror] = useState({ enabled: false, fx: {} });
  const [f, setF] = useState({
    customer: bit.customer || "",
    price: bit.price || "",
    currency: bit.currency || "",
    sold_date: new Date().toISOString().slice(0, 10),
    order_no: "", shipment_no: "", note: "",
    erp_destination: "none",
  });
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));

  // Buyer autocomplete pulls the ERP client list; settings tell us whether the
  // ERP mirror is on (so we only show the destination picker when it matters).
  useEffect(() => {
    axios.get(`${API}/clients`)
      .then(r => setClientNames((r.data || []).map(c => c.name).filter(Boolean)))
      .catch(() => {});
    axios.get(`${API}/settings`)
      .then(r => setMirror({ enabled: !!r.data?.sc_mirror_enabled, fx: r.data?.fx_rates || {} }))
      .catch(() => {});
  }, []);

  // USD preview using the stored rate (only meaningful when mirroring).
  const usdPreview = (() => {
    const amt = parseFloat(String(f.price).replace(/,/g, ""));
    const rate = mirror.fx[(f.currency || "").toUpperCase()];
    if (!amt || !rate) return null;
    return Math.round(amt * rate).toLocaleString();
  })();

  const submit = async (e) => {
    e.preventDefault();
    if (!f.customer.trim()) { toast.error("Customer is required"); return; }
    // If the mirror is on, the seller must choose where it lands in the ERP.
    if (mirror.enabled && f.erp_destination === "none") {
      toast.error("Choose where this sale goes in the ERP");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/stock-center/bits/${bit.id}/sell`, f);
      toast.success(mirror.enabled && f.erp_destination !== "none" ? "Sold — mirrored to ERP" : "Bit marked sold");
      onDone?.(); onClose();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to record sale"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      {/* Capped height + scrollable body + pinned footer (same pattern as Receive)
          so the form — esp. with the ERP-mirror panel expanded — never spills past
          a phone screen with the Confirm/Cancel buttons unreachable. The short
          paired grids (Price+Currency etc.) stay 2-up; they read fine narrow. */}
      <DialogContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))] max-w-md flex flex-col max-h-[90vh] p-0" data-testid="sell-bit-modal">
        <DialogHeader className="px-6 pt-6 shrink-0">
          <DialogTitle className="flex items-center gap-2"><Receipt className="w-5 h-5 text-[#0F6E56]" /> Mark bit sold</DialogTitle>
          <DialogDescription>
            <span className="font-mono-data text-[hsl(var(--foreground))]">{bit.marking_no || "no serial"}</span> · {bit.warehouse} · {bit.bit_size_in || bit.bit_size_mm || ""}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="flex flex-col min-h-0 flex-1">
          <div className="space-y-3 overflow-y-auto px-6 py-1">
          <div><span className={label}>Customer *</span>
            <input value={f.customer} onChange={e => set("customer", e.target.value)} className={field}
                   placeholder="Search clients or type a new buyer" autoFocus data-testid="sell-customer"
                   list="sc-client-list" autoComplete="off" />
            <datalist id="sc-client-list">
              {clientNames.map(n => <option key={n} value={n} />)}
            </datalist>
            <p className="text-[10px] text-[hsl(var(--muted-foreground))] mt-1">Suggests from RockLink clients — or type a new name.</p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><span className={label}>Price</span>
              <input value={f.price} onChange={e => set("price", e.target.value)} className={field} placeholder="Amount" /></div>
            <div><span className={label}>Currency</span>
              <select value={f.currency} onChange={e => set("currency", e.target.value)} className={field}>
                <option value="">—</option>
                {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div><span className={label}>Sold date</span>
              <input type="date" value={f.sold_date} onChange={e => set("sold_date", e.target.value)} className={field} /></div>
            <div><span className={label}>Order no.</span>
              <input value={f.order_no} onChange={e => set("order_no", e.target.value)} className={field} /></div>
          </div>
          <div><span className={label}>Note</span>
            <input value={f.note} onChange={e => set("note", e.target.value)} className={field} placeholder="Optional" /></div>

          {mirror.enabled ? (
            <div className="rounded-md border border-[#0F6E56]/30 bg-[#0F6E56]/[0.04] px-3 py-2.5">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[11px] font-semibold text-[hsl(var(--foreground))]">Where does this go in RockLink ERP?</span>
                {usdPreview && <span className="text-[11px] font-mono-data text-[#0F6E56]">≈ USD {usdPreview}</span>}
              </div>
              <div className="grid grid-cols-2 gap-2" data-testid="erp-destination">
                {[["dispatch", "Ship via Dispatch", "Enters at Dispatch"], ["completed", "Already shipped", "Lands at Completed"]].map(([val, t, sub]) => (
                  <button type="button" key={val} onClick={() => set("erp_destination", val)}
                    className={`text-left rounded-md border p-2 transition-all ${f.erp_destination === val ? "border-[#0F6E56] bg-[#0F6E56]/[0.08]" : "border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))]/40"}`}
                    data-testid={`erp-dest-${val}`}>
                    <div className="text-[12px] font-medium text-[hsl(var(--foreground))]">{t}</div>
                    <div className="text-[10px] text-[hsl(var(--muted-foreground))]">{sub}</div>
                  </button>
                ))}
              </div>
              <p className="text-[10px] text-[hsl(var(--muted-foreground))] mt-2">Mirrors to the ERP under the Rockpecker Drilling client group, tagged as a stock-out sale.</p>
            </div>
          ) : (
            <div className="text-[11px] text-[hsl(var(--muted-foreground))] bg-[hsl(var(--muted))]/30 rounded-md px-3 py-2">
              Records the sale in the Stock Center only. ERP mirroring is configured separately by an admin.
            </div>
          )}
          </div>
          {/* Pinned footer — always reachable while the body scrolls. */}
          <DialogFooter className="gap-2 px-6 py-4 border-t border-[hsl(var(--border))] shrink-0 bg-[hsl(var(--card))]">
            <button type="button" onClick={onClose} className="h-9 px-4 rounded-md text-[13px] border border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))]">Cancel</button>
            <SubmitBtn saving={saving} icon={Receipt}>Confirm sale</SubmitBtn>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ── Full bit detail — view all fields, edit inline ──
const DETAIL_GROUPS = [
  { title: "Identity", fields: [
    ["marking_no", "Marking no."], ["design_no", "Design no."], ["status", "Status"],
    ["body_type", "Body type"], ["supplier", "Supplier"], ["shelf_location", "Shelf location"],
  ]},
  { title: "Dimensions", fields: [
    ["bit_size_in", "Bit size (in)"], ["bit_size_mm", "Bit size (mm)"], ["size_label", "Type / label"],
    ["blades", "Blades"], ["thread_size", "Thread size"], ["unit_weight", "Unit weight"], ["box_size", "Box size"],
  ]},
  { title: "Cutters & nozzles", fields: [
    ["gauge_cutter_qty", "Gauge cutter qty"], ["gauge_cutter_size", "Gauge cutter size"],
    ["pdc_qty", "PDC qty"], ["pdc_size", "PDC size"],
    ["dome_qty", "Dome qty"], ["dome_size", "Dome size"],
    ["nozzle_count", "Nozzle count"], ["nozzle_size", "Nozzle size"],
  ]},
  { title: "Commercial", fields: [
    ["customer", "Customer"], ["order_no", "Order no."], ["sold_date", "Sold date"],
    ["shipment_no", "Shipment no."], ["spec_sheet", "Spec sheet"], ["remarks", "Remarks"],
  ]},
];
const STATUS_OPTS = [["in_stock","In stock"],["unfinished","Unfinished"],["in_transit","In transit"],["overstock","Overstock"],["sold","Sold"],["missing","Missing"]];

// One inline-editable cell (Notion style). Click the value → it becomes an
// input backed by a datalist of existing values (suggest-but-allow-new). Saves
// on blur/Enter (PATCHes just this field); Escape cancels. Read-only fields
// (marking, dates handled normally) still edit as plain text.
function InlineCell({ bitId, k, label: lbl, value, options, editable, onPatched }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value ?? "");
  const [saving, setSaving] = useState(false);
  const listId = `sc-sg-${k}`;

  React.useEffect(() => { setVal(value ?? ""); }, [value]);

  const commit = async () => {
    const next = (val ?? "").toString();
    if (next === (value ?? "").toString()) { setEditing(false); return; }
    setSaving(true);
    try {
      await axios.patch(`${API}/stock-center/bits/${bitId}`, { [k]: next });
      onPatched?.(k, next);
      setEditing(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
      setVal(value ?? "");
    } finally { setSaving(false); }
  };

  const cancel = () => { setVal(value ?? ""); setEditing(false); };

  const isUrl = k === "spec_sheet" && String(value || "").startsWith("http");
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-[hsl(var(--muted-foreground))]/70 mb-0.5">{lbl}</div>
      {editing ? (
        <div className="flex items-center gap-1">
          {k === "status" ? (
            <select autoFocus value={val} onChange={(e) => setVal(e.target.value)} onBlur={commit}
                    onKeyDown={(e) => { if (e.key === "Enter") commit(); if (e.key === "Escape") cancel(); }}
                    className="h-8 px-2 text-[13px] rounded-md bg-[hsl(var(--background))] border border-[#0F6E56] focus:outline-none w-full">
              {STATUS_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          ) : (
            <>
              <input autoFocus value={val} onChange={(e) => setVal(e.target.value)} onBlur={commit}
                     onKeyDown={(e) => { if (e.key === "Enter") commit(); if (e.key === "Escape") cancel(); }}
                     list={options && options.length ? listId : undefined} autoComplete="off"
                     className="h-8 px-2 text-[13px] font-mono-data rounded-md bg-[hsl(var(--background))] border border-[#0F6E56] focus:outline-none w-full" />
              {options && options.length > 0 && (
                <datalist id={listId}>{options.map((o) => <option key={o} value={o} />)}</datalist>
              )}
            </>
          )}
          {saving && <Loader2 className="w-3.5 h-3.5 animate-spin text-[hsl(var(--muted-foreground))] shrink-0" />}
        </div>
      ) : isUrl ? (
        <a href={value} target="_blank" rel="noreferrer" className="text-[#0F6E56] inline-flex items-center gap-1 hover:underline text-[13px]">link <ExternalLink className="w-3 h-3" /></a>
      ) : (
        <button
          type="button"
          disabled={!editable}
          onClick={() => editable && setEditing(true)}
          className={`group/ic text-left w-full rounded px-1 -mx-1 py-0.5 transition-colors ${editable ? "hover:bg-[#0F6E56]/[0.07] cursor-text" : "cursor-default"}`}
          title={editable ? "Click to edit" : undefined}
        >
          {(value === "" || value == null)
            ? <span className="text-[hsl(var(--muted-foreground))]/40 text-[13px]">{editable ? "—  add" : "—"}</span>
            : <span className="font-mono-data text-[13px] text-[hsl(var(--foreground))]">{String(value)}</span>}
        </button>
      )}
    </div>
  );
}

// This bit's own transaction history — a compact timeline inside the detail
// drawer ("what happened to THIS serial"). Reuses the per-bit /transactions
// endpoint; newest first. Read-only.
const HIST_META = {
  receive:   { verb: "Received",       icon: Package,        color: "#185FA5" },
  sell:      { verb: "Sold",           icon: Receipt,        color: "#0F6E56" },
  status:    { verb: "Status changed", icon: RefreshCw,      color: "#BA7517" },
  duplicate: { verb: "Duplicated",     icon: Copy,           color: "#5F5E5A" },
  transfer:  { verb: "Transferred",    icon: ArrowLeftRight, color: "#534AB7" },
};
function histRel(iso) {
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return "";
  const s = Math.floor((Date.now() - t) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  const d = Math.floor(s / 86400);
  if (d === 1) return "yesterday";
  if (d < 30) return `${d}d ago`;
  return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}
function histDetail(t) {
  if (t.type === "sell" && t.customer) return `to ${t.customer}`;
  if (t.type === "status" && (t.old_status || t.new_status)) return `${t.old_status || "—"} → ${t.new_status || "—"}`;
  if (t.type === "transfer" && (t.from_warehouse || t.to_warehouse)) return `${t.from_warehouse || "—"} → ${t.to_warehouse || "—"}`;
  return t.note || "";
}
function BitHistory({ bitId }) {
  const [txns, setTxns] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    let live = true;
    axios.get(`${API}/stock-center/bits/${bitId}/transactions`)
      .then((r) => { if (live) setTxns(r.data || []); })
      .catch(() => { if (live) setErr(true); });
    return () => { live = false; };
  }, [bitId]);

  if (err) return null; // history is supplementary — never block the drawer
  return (
    <div>
      <p className="text-[10px] uppercase tracking-widest font-bold text-[hsl(var(--muted-foreground))] mb-2 inline-flex items-center gap-1.5">
        <Clock className="w-3 h-3" aria-hidden="true" /> History
      </p>
      {txns === null ? (
        <div className="space-y-2 animate-pulse" aria-hidden="true">
          {[0, 1].map((i) => (
            <div key={i} className="flex items-center gap-2.5">
              <span className="w-6 h-6 rounded-full bg-[hsl(var(--muted-foreground))]/15 shrink-0" />
              <span className="h-3 rounded bg-[hsl(var(--muted-foreground))]/15" style={{ width: i ? "44%" : "58%" }} />
            </div>
          ))}
        </div>
      ) : txns.length === 0 ? (
        <p className="text-[12px] text-[hsl(var(--muted-foreground))]">No recorded history.</p>
      ) : (
        <ol className="space-y-2.5">
          {txns.map((t) => {
            const m = HIST_META[t.type] || { verb: t.type || "Updated", icon: Clock, color: "#5F5E5A" };
            const Icon = m.icon;
            const detail = histDetail(t);
            return (
              <li key={t.id} className="flex items-start gap-2.5">
                <span className="flex items-center justify-center w-6 h-6 rounded-full shrink-0 mt-0.5"
                      style={{ background: `${m.color}1a`, color: m.color }} aria-hidden="true">
                  <Icon className="w-3 h-3" />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-[12.5px] text-[hsl(var(--foreground))]">
                    <span className="font-semibold" style={{ color: m.color }}>{m.verb}</span>
                    {detail && <span className="text-[hsl(var(--muted-foreground))]"> {detail}</span>}
                  </div>
                  <div className="text-[11px] text-[hsl(var(--muted-foreground))]">
                    {t.user || "Unknown"}{t.user_role ? ` (${t.user_role})` : ""} · <span title={new Date(t.timestamp).toLocaleString()}>{histRel(t.timestamp)}</span>
                  </div>
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}

// Step 1 of a transfer: send a bit to another warehouse. The bit moves to the
// destination immediately (status → in transit); the destination confirms
// arrival later. Price/currency are left untouched (kept in original currency).
export function TransferBitModal({ bit, onClose, onDone }) {
  const [saving, setSaving] = useState(false);
  const [dest, setDest] = useState("");
  const [note, setNote] = useState("");
  const others = WAREHOUSES.filter((w) => w.code !== bit.warehouse);
  const srcName = (WAREHOUSES.find((w) => w.code === bit.warehouse) || {}).name || bit.warehouse;
  const destName = (WAREHOUSES.find((w) => w.code === dest) || {}).name || "—";

  const submit = async () => {
    if (!dest) { toast.error("Choose a destination warehouse"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/stock-center/bits/${bit.id}/transfer`, { to_warehouse: dest, note: note.trim() });
      toast.success(`Sent to ${destName} — confirm arrival there when it lands`);
      onDone?.(); onClose();
    } catch (err) { toast.error(err?.response?.data?.detail || "Transfer failed"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))] max-w-md flex flex-col max-h-[90vh] p-0" data-testid="bit-transfer-modal">
        <DialogHeader className="px-6 pt-6 shrink-0">
          <DialogTitle className="flex items-center gap-2"><ArrowLeftRight className="w-5 h-5 text-[#534AB7]" /> Transfer bit</DialogTitle>
          <DialogDescription>
            Marking <span className="font-mono-data text-[hsl(var(--foreground))]">{bit.marking_no || "—"}</span>
            {bit.blades ? ` · ${bit.blades}` : ""}{bit.bit_size_in ? ` · ${bit.bit_size_in}"` : ""}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 overflow-y-auto px-6 py-2 min-h-0 flex-1">
          <div className="flex items-center gap-3">
            <div className="flex-1 text-center rounded-lg bg-[hsl(var(--muted))]/40 py-2.5">
              <div className="text-[11px] text-[hsl(var(--muted-foreground))]">From</div>
              <div className="text-[14px] font-medium text-[hsl(var(--foreground))]">{srcName}</div>
            </div>
            <ArrowRight className="w-4 h-4 text-[#534AB7] shrink-0" />
            <div className="flex-1">
              <div className={label}>To</div>
              <select className={field} value={dest} onChange={(e) => setDest(e.target.value)} data-testid="transfer-dest">
                <option value="">Choose…</option>
                {others.map((w) => <option key={w.code} value={w.code}>{w.name}</option>)}
              </select>
            </div>
          </div>
          <div>
            <div className={label}>Note <span className="text-[hsl(var(--muted-foreground))]/60">(optional)</span></div>
            <input className={field} value={note} onChange={(e) => setNote(e.target.value)} placeholder="shipment ref, courier…" />
          </div>
          <div className="text-[11px] text-[hsl(var(--muted-foreground))] leading-relaxed flex gap-1.5 rounded-lg bg-[#534AB7]/8 px-3 py-2">
            <Info className="w-3.5 h-3.5 mt-0.5 shrink-0 text-[#534AB7]" />
            <span>Price stays in its original currency. The bit is marked <span className="font-mono-data">in transit</span> until the destination confirms it arrived.</span>
          </div>
        </div>
        <DialogFooter className="gap-2 px-6 py-4 border-t border-[hsl(var(--border))] shrink-0 bg-[hsl(var(--card))]">
          <button type="button" onClick={onClose} className="h-9 px-4 rounded-md text-[13px] border border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))]">Cancel</button>
          <button type="button" disabled={saving || !dest} onClick={submit}
            className="inline-flex items-center gap-1.5 h-9 px-4 rounded-md text-[13px] font-semibold text-white disabled:opacity-50" style={{ backgroundColor: "#534AB7" }}>
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send{dest ? ` to ${destName}` : ""}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function BitDetailModal({ bit, canManage, canViewCost, suggestions = {}, onClose, onSaved }) {
  // Local copy so inline edits reflect immediately without refetching/closing.
  const [data, setData] = useState({ ...bit });
  const onPatched = (k, v) => {
    setData((p) => ({ ...p, [k]: v }));
    toast.success("Saved", { duration: 1200 });
    onSaved?.();  // refresh the table behind the drawer (drawer stays open)
  };
  // marking_no is the identity — editable; everything else editable for managers.
  const cellProps = (k, lbl) => ({
    bitId: bit.id, k, label: lbl, value: data[k] ?? "",
    options: suggestions[k] || [], editable: !!canManage, onPatched,
  });

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))] max-w-2xl" data-testid="bit-detail-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2.5">
            <span className="font-mono-data text-[15px] px-2 py-0.5 rounded-md bg-[hsl(var(--muted))]/50 border border-[hsl(var(--border))]">{data.marking_no || "no serial"}</span>
            <span className="text-[13px] font-normal text-[hsl(var(--muted-foreground))]">{data.warehouse} · {data.design_no || ""}</span>
            {data.needs_review && <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#FAEEDA] text-[#854F0B]">needs review</span>}
          </DialogTitle>
          {canManage && <p className="text-[11px] text-[hsl(var(--muted-foreground))]">Click any value to edit. Suggestions help keep entries consistent.</p>}
        </DialogHeader>
        <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
          {DETAIL_GROUPS.map(g => (
            <div key={g.title}>
              <p className="text-[10px] uppercase tracking-widest font-bold text-[hsl(var(--muted-foreground))] mb-2">{g.title}</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-3">
                {g.fields.map(([k, lbl]) => <InlineCell key={k} {...cellProps(k, lbl)} />)}
              </div>
            </div>
          ))}
          {canViewCost && (
            <div>
              <p className="text-[10px] uppercase tracking-widest font-bold text-[hsl(var(--muted-foreground))] mb-2">Value</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-3">
                <InlineCell {...cellProps("price", "Price")} />
                <InlineCell {...cellProps("currency", "Currency")} />
              </div>
            </div>
          )}
          {/* This bit's own transaction history — separated by a rule. */}
          <div className="pt-3 border-t border-[hsl(var(--border))]">
            <BitHistory bitId={bit.id} />
          </div>
        </div>
        <DialogFooter>
          <button type="button" onClick={onClose} className="h-9 px-4 rounded-md text-[13px] border border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))]">Close</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── CSV import (mirrors StoresImportModal: Excel-hardened parse) ──
// Excel-hardened CSV parse: strips the UTF-8 BOM, normalises newlines,
// auto-detects the delimiter (, ; or tab), honours quoted fields.
function parseCSV(text) {
  const raw = String(text || "").replace(/^﻿/, "").replace(/\r\n/g, "\n").replace(/\r/g, "\n");
  const lines = raw.split("\n").filter((l) => l.trim().length);
  if (!lines.length) return { headers: [], rows: [] };
  const head = lines[0];
  const n = (ch) => head.split(ch).length - 1;
  const delim = n(";") > n(",") ? ";" : n("\t") > n(",") ? "\t" : ",";
  const parseLine = (line) => {
    const out = []; let cur = ""; let inQ = false;
    for (let i = 0; i < line.length; i++) {
      const c = line[i];
      if (inQ) { if (c === '"') { if (line[i + 1] === '"') { cur += '"'; i++; } else inQ = false; } else cur += c; }
      else { if (c === '"') inQ = true; else if (c === delim) { out.push(cur); cur = ""; } else cur += c; }
    }
    out.push(cur); return out.map((s) => s.trim().replace(/^["']|["']$/g, ""));
  };
  const cleanHead = (h) => h.toLowerCase().replace(/^﻿/, "").trim().replace(/^["']|["']$/g, "").replace(/\s+/g, "_");
  return { headers: parseLine(lines[0]).map(cleanHead), rows: lines.slice(1).map(parseLine) };
}

// Columns the import understands. Matches the extractor's clean CSV output.
const BIT_IMPORT_COLS = [
  "warehouse", "marking_no", "design_no", "status", "bit_size_in", "size_label", "bit_size_mm",
  "body_type", "blades", "thread_size", "nozzle_count", "nozzle_size", "gauge_cutter_qty",
  "gauge_cutter_size", "pdc_qty", "pdc_size", "dome_qty", "dome_size", "unit_weight", "box_size",
  "shelf_location", "supplier", "spec_sheet", "spec_sheet_url", "customer", "order_no",
  "sold_date", "shipment_no", "price", "currency", "remarks", "flags",
];
const WAREHOUSE_CODES = ["UK", "AUS", "CH", "CA", "IN"];

function rowToBit(headers, values) {
  const o = {}; headers.forEach((h, i) => { o[h] = values[i] ?? ""; });
  const bit = {};
  BIT_IMPORT_COLS.forEach((c) => { if (c !== "flags") bit[c] = (o[c] ?? "").toString().trim(); });
  bit.warehouse = (bit.warehouse || "").toUpperCase();
  // flags may arrive as "incomplete | marking_collision"
  bit.flags = (o.flags || "").split(/[|,]/).map((s) => s.trim()).filter(Boolean);
  return bit;
}

// Human-friendly labels for the fields an upsert can change (diff chips).
const SC_FIELD_LABELS = {
  design_no: "design", bit_size_in: "size (in)", bit_size_mm: "size (mm)", size_label: "type",
  body_type: "body", blades: "blades", thread_size: "thread", nozzle_count: "nozzles",
  nozzle_size: "nozzle size", gauge_cutter_qty: "gauge qty", gauge_cutter_size: "gauge size",
  pdc_qty: "PDC qty", pdc_size: "PDC size", dome_qty: "dome qty", dome_size: "dome size",
  unit_weight: "weight", box_size: "box", shelf_location: "shelf", supplier: "supplier",
  spec_sheet: "spec", spec_sheet_url: "spec link", remarks: "remarks", flags: "flags",
};
const fmtVal = (v) => {
  if (Array.isArray(v)) return v.length ? v.join(", ") : "—";
  const s = (v ?? "").toString().trim();
  return s.length ? s : "—";
};

export function BitImportModal({ onClose, onDone }) {
  const [parsed, setParsed] = useState(null);     // { total, valid, errorCount }
  const [diff, setDiff] = useState(null);         // server preview result
  const [busy, setBusy] = useState(false);        // covers preview + import
  const [showChanges, setShowChanges] = useState(false);
  const fileRef = React.useRef(null);

  const downloadTemplate = () => {
    const ex = ["UK", "12345", "0765.00", "in_stock", "4.75", "PDC DRAG BIT", "120.65", "FINISHED",
      "4 Blade", "2-3/8\" API REG PIN", "3", "6 MM", "12", "1208", "15", "1308", "", "", "5.9", "130x130x300",
      "rack 2, shelf D", "Rockpecker", "R-01.0765.00", "", "", "", "", "", "620", "EUR", "", ""];
    const csv = [BIT_IMPORT_COLS.join(","), ex.join(",")].join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a"); a.href = url; a.download = "stock_center_import_template.csv"; a.click(); URL.revokeObjectURL(url);
  };

  // Parse the file, then ask the server what would change (dry run, writes nothing).
  const handleFile = async (file) => {
    if (!file) return;
    setDiff(null); setShowChanges(false);
    const text = await file.text();
    const { headers, rows } = parseCSV(text);
    if (!headers.includes("warehouse")) { toast.error("CSV needs a 'warehouse' column — use the template"); return; }
    const bits = rows.map((r) => rowToBit(headers, r));
    const valid = bits.filter((b) => WAREHOUSE_CODES.includes(b.warehouse) && (b.marking_no || b.design_no));
    setParsed({ total: bits.length, valid, errorCount: bits.length - valid.length });
    if (!valid.length) return;
    setBusy(true);
    try {
      // Preview can run in one call (writes nothing); cap matches the import.
      const res = await axios.post(`${API}/stock-center/bits/bulk/preview`, valid.slice(0, 5000));
      setDiff(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't preview the import");
    } finally { setBusy(false); }
  };

  const runImport = async (mode) => {
    if (!parsed?.valid?.length) return;
    setBusy(true);
    try {
      const CHUNK = 500; let inserted = 0, updated = 0, skipped = 0;
      for (let i = 0; i < parsed.valid.length; i += CHUNK) {
        const res = await axios.post(`${API}/stock-center/bits/bulk?mode=${mode}`, parsed.valid.slice(i, i + CHUNK));
        inserted += res.data?.count ?? 0; updated += res.data?.updated ?? 0; skipped += res.data?.skipped ?? 0;
      }
      const bits = [];
      if (inserted) bits.push(`${inserted} added`);
      if (updated) bits.push(`${updated} updated`);
      toast.success(bits.length ? `Import done — ${bits.join(" · ")}${skipped ? ` · ${skipped} unchanged` : ""}` : "Nothing to import");
      onDone?.(); onClose();
    } catch (err) { toast.error(err?.response?.data?.detail || "Import failed"); }
    finally { setBusy(false); }
  };

  const StatCard = ({ n, label, tone }) => (
    <div className={`rounded-lg px-3 py-2 ${tone.bg}`}>
      <div className={`text-[20px] leading-none font-mono-data font-medium ${tone.fg}`}>{n}</div>
      <div className={`text-[11px] mt-1 ${tone.fg}`}>{label}</div>
    </div>
  );

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      {/* Same cap-height + scroll + pinned-footer pattern as the other Stock
          Center modals, so it never spills past a small screen. */}
      <DialogContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))] max-w-lg flex flex-col max-h-[90vh] p-0" data-testid="bit-import-modal">
        <DialogHeader className="px-6 pt-6 shrink-0">
          <DialogTitle className="flex items-center gap-2"><FileSpreadsheet className="w-5 h-5 text-[#0F6E56]" /> Import bits from CSV</DialogTitle>
          <DialogDescription>Upload a cleaned stock CSV. You'll see exactly what's new and what a corrected sheet would change before anything is written.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 overflow-y-auto px-6 py-1 min-h-0 flex-1">
          <button type="button" onClick={downloadTemplate} className="inline-flex items-center gap-1.5 text-[12.5px] font-medium px-3 py-1.5 rounded-lg border border-[hsl(var(--border))] hover:bg-[hsl(var(--accent))] text-[hsl(var(--foreground))]"><Download className="w-4 h-4" /> Download CSV template</button>
          <div onClick={() => fileRef.current?.click()} className="cursor-pointer rounded-lg border-2 border-dashed border-[hsl(var(--border))] hover:border-[#0F6E56]/50 px-4 py-6 text-center transition-colors">
            <Upload className="w-5 h-5 mx-auto text-[hsl(var(--muted-foreground))] mb-1" />
            <div className="text-[12.5px] text-[hsl(var(--muted-foreground))]">Click to choose a .csv file</div>
            <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => handleFile(e.target.files?.[0])} />
          </div>
          <p className="text-[11px] text-[hsl(var(--muted-foreground))] leading-relaxed">
            Each row needs a valid <span className="font-mono-data">warehouse</span> (UK/AUS/CH/CA/IN) and a marking or design number. Excel quirks (delimiter, encoding marks) are handled automatically.
          </p>

          {parsed && parsed.errorCount > 0 && (
            <div className="flex items-center gap-2 text-[12.5px] text-amber-600"><AlertTriangle className="w-4 h-4" /> {parsed.errorCount} row(s) will be skipped (bad warehouse or no marking/design)</div>
          )}

          {busy && !diff && parsed?.valid?.length > 0 && (
            <div className="flex items-center gap-2 text-[12.5px] text-[hsl(var(--muted-foreground))]"><Loader2 className="w-4 h-4 animate-spin" /> Checking what would change…</div>
          )}

          {diff && (
            <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-3.5 space-y-3">
              <div className="flex items-center gap-2 text-[13px] font-medium text-[hsl(var(--foreground))]"><FileDiff className="w-4 h-4 text-[#0F6E56]" /> Review changes</div>
              <div className="grid grid-cols-4 gap-2">
                <StatCard n={diff.new} label="new" tone={{ bg: "bg-blue-500/10", fg: "text-blue-600" }} />
                <StatCard n={diff.changed} label="changed" tone={{ bg: "bg-amber-500/10", fg: "text-amber-600" }} />
                <StatCard n={diff.unchanged} label="unchanged" tone={{ bg: "bg-[hsl(var(--muted))]/40", fg: "text-[hsl(var(--muted-foreground))]" }} />
                <StatCard n={diff.skipped} label="skipped" tone={{ bg: "bg-[hsl(var(--muted))]/40", fg: "text-[hsl(var(--muted-foreground))]" }} />
              </div>

              {diff.changed > 0 && (
                <div>
                  <button type="button" onClick={() => setShowChanges((s) => !s)}
                    className="inline-flex items-center gap-1.5 text-[12px] font-medium text-[hsl(var(--foreground))]">
                    <FileDiff className="w-3.5 h-3.5" /> {diff.changed} row{diff.changed !== 1 ? "s" : ""} will be updated
                    <span className="text-[hsl(var(--muted-foreground))]">{showChanges ? "(hide)" : "(show)"}</span>
                  </button>
                  {showChanges && (
                    <div className="mt-2 rounded-lg border border-[hsl(var(--border))] divide-y divide-[hsl(var(--border))]">
                      {diff.changes.map((c, idx) => (
                        <div key={idx} className="px-3 py-2 flex items-start gap-2.5">
                          <span className="font-mono-data text-[12px] text-[hsl(var(--foreground))] min-w-[58px]">{c.marking_no}</span>
                          <span className="text-[11px] text-[hsl(var(--muted-foreground))] whitespace-nowrap">{[c.blades, c.bit_size_in && `${c.bit_size_in}"`].filter(Boolean).join(" · ")}</span>
                          <div className="flex flex-wrap gap-1.5 text-[11px]">
                            {Object.entries(c.fields).map(([f, ov]) => (
                              <span key={f} className="px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-700">
                                {SC_FIELD_LABELS[f] || f} <span className="line-through opacity-60">{fmtVal(ov.old)}</span> → {fmtVal(ov.new)}
                              </span>
                            ))}
                          </div>
                        </div>
                      ))}
                      {diff.changed > diff.changes.length && (
                        <div className="px-3 py-2 text-[11px] text-[hsl(var(--muted-foreground))]">+ {diff.changed - diff.changes.length} more changed row(s)</div>
                      )}
                    </div>
                  )}
                  <p className="text-[11px] text-[hsl(var(--muted-foreground))] leading-relaxed mt-2">
                    Updates only touch spec fields (size, thread, shelf, supplier…). Status, customer and sale details are never overwritten, and blank cells are left as-is.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
        <DialogFooter className="gap-2 px-6 py-4 border-t border-[hsl(var(--border))] shrink-0 bg-[hsl(var(--card))] flex-col sm:flex-row">
          <button type="button" onClick={onClose} className="h-9 px-4 rounded-md text-[13px] border border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))]">Cancel</button>
          <button type="button" disabled={!diff || busy || diff.new === 0} onClick={() => runImport("insert")}
            className="inline-flex items-center justify-center gap-1.5 text-[13px] font-medium px-4 py-2 rounded-md disabled:opacity-50 border border-[hsl(var(--border))] text-[hsl(var(--foreground))] hover:bg-[hsl(var(--accent))]">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Import {diff?.new || 0} new only
          </button>
          <button type="button" disabled={!diff || busy || (diff.new === 0 && diff.changed === 0)} onClick={() => runImport("upsert")}
            className="inline-flex items-center justify-center gap-1.5 text-[13px] font-semibold px-4 py-2 rounded-md disabled:opacity-50 text-white" style={{ backgroundColor: "#0F6E56" }}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Apply {diff?.new || 0} new + {diff?.changed || 0} updates
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
